import { 
  BarChart3, 
  Package, 
  Store, 
  Sparkles, 
  Settings,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Wallet,
  Bell,
  Gift,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  subItems?: { id: string; label: string; icon?: React.ElementType }[];
}

type OrdersSubView = "requests" | "vouchers" | "refunds";
type InsightsSubView = "analytics" | "customers";
type SettingsSubView = "billing" | "preferences" | "notifications" | "info";

export type PlatformTheme = "shopify" | "woocommerce" | "standalone" | "professional";

interface MerchantSidebarProps {
  activeTab: string;
  ordersSubView: OrdersSubView;
  insightsSubView: InsightsSubView;
  settingsSubView: SettingsSubView;
  onTabChange: (tab: string, subView?: string) => void;
  onOrdersSubViewChange: (view: OrdersSubView) => void;
  onInsightsSubViewChange: (view: InsightsSubView) => void;
  onSettingsSubViewChange: (view: SettingsSubView) => void;
  pendingOrdersCount: number;
  pendingRefundsCount: number;
  pendingFeesCount: number;
  canManageProducts: boolean;
  canAccessSettings: boolean;
  widgetNeedsUpdate?: boolean;
  platformTheme?: PlatformTheme;
}

// Helper to get theme classes based on platform
const getThemeClasses = (platformTheme: PlatformTheme) => {
  switch (platformTheme) {
    case "shopify":
      return {
        bg: "bg-shopify",
        text: "text-shopify-foreground",
        bgLight: "bg-shopify/10",
        textAccent: "text-shopify",
        badgeBg: "bg-shopify-foreground/20",
      };
    case "woocommerce":
      return {
        bg: "bg-woocommerce",
        text: "text-woocommerce-foreground",
        bgLight: "bg-woocommerce/10",
        textAccent: "text-woocommerce",
        badgeBg: "bg-woocommerce-foreground/20",
      };
    case "professional":
      return {
        bg: "bg-professional",
        text: "text-professional-foreground",
        bgLight: "bg-professional/10",
        textAccent: "text-professional",
        badgeBg: "bg-professional-foreground/20",
      };
    default: // standalone / direct
      return {
        bg: "bg-primary",
        text: "text-primary-foreground",
        bgLight: "bg-primary/10",
        textAccent: "text-primary",
        badgeBg: "bg-primary-foreground/20",
      };
  }
};

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function MerchantSidebar({
  activeTab,
  ordersSubView,
  insightsSubView,
  settingsSubView,
  onTabChange,
  onOrdersSubViewChange,
  onInsightsSubViewChange,
  onSettingsSubViewChange,
  pendingOrdersCount,
  pendingRefundsCount,
  pendingFeesCount,
  canManageProducts,
  canAccessSettings,
  widgetNeedsUpdate = false,
  platformTheme = "standalone",
}: MerchantSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const themeClasses = getThemeClasses(platformTheme);

  const navItems: NavItem[] = [
    {
      id: "overview",
      label: "Overview",
      icon: BarChart3,
    },
    {
      id: "orders",
      label: "Orders",
      icon: Package,
      badge: (pendingOrdersCount + pendingRefundsCount) > 0 ? (pendingOrdersCount + pendingRefundsCount) : undefined,
      subItems: [
        { id: "requests", label: "All Orders", icon: Package },
        { id: "vouchers", label: "Vouchers", icon: Gift },
        { id: "refunds", label: "Refunds", icon: RotateCcw, badge: pendingRefundsCount > 0 ? pendingRefundsCount : undefined } as any,
      ],
    },
    ...(canManageProducts ? [{
      id: "products",
      label: "Products",
      icon: Store,
    }] : []),
    {
      id: "insights",
      label: "Insights",
      icon: Sparkles,
      subItems: [
        { id: "analytics", label: "Analytics", icon: Sparkles },
        { id: "customers", label: "Customers", icon: UserCheck },
      ],
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      // Show badge for pending fees OR widget updates
      badge: (pendingFeesCount > 0 || widgetNeedsUpdate) ? (pendingFeesCount || (widgetNeedsUpdate ? 1 : 0)) : undefined,
      subItems: canAccessSettings ? [
        { id: "billing", label: "Billing", icon: Wallet },
        { id: "preferences", label: "Preferences", icon: Settings },
        { id: "notifications", label: "Notifications", icon: Bell },
        { id: "info", label: "Business Info", icon: Store },
      ] : [
        { id: "notifications", label: "Notifications", icon: Bell },
      ],
    },
  ];

  const handleItemClick = (item: NavItem, subItemId?: string) => {
    // If clicking a subItem, pass both tab and subView to parent for combined URL update
    if (subItemId) {
      onTabChange(item.id, subItemId);
    } else {
      // For top-level items without subItems, or items with subItems (default to first)
      if (item.subItems && item.subItems.length > 0) {
        // When clicking parent with subItems, navigate to the current/default subview
        const currentSubView = getActiveSubView(item.id) || item.subItems[0].id;
        onTabChange(item.id, currentSubView);
      } else {
        onTabChange(item.id);
      }
    }
  };

  const getActiveSubView = (itemId: string) => {
    switch (itemId) {
      case "orders": return ordersSubView;
      case "insights": return insightsSubView;
      case "settings": return settingsSubView;
      default: return "";
    }
  };

  return (
    <aside 
      className={cn(
        "bg-card border-r h-full flex flex-col transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-56"
      )}
    >
      {/* Collapse Toggle */}
      <div className="flex items-center justify-end p-2 border-b">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;
          const hasSubItems = item.subItems && item.subItems.length > 0;

          return (
            <div key={item.id}>
              {/* Main Nav Item */}
              <button
                onClick={() => handleItemClick(item, hasSubItems ? item.subItems![0].id : undefined)}
                data-tour={`${item.id}-tab`}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? `${themeClasses.bg} ${themeClasses.text}`
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <Badge 
                        variant="secondary" 
                        className={cn(
                          "h-5 px-1.5 text-xs",
                          isActive && `${themeClasses.badgeBg} ${themeClasses.text}`
                        )}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
                {collapsed && item.badge && (
                  <span className={cn(
                    "absolute top-0 right-0 h-2 w-2 rounded-full",
                    themeClasses.bg
                  )} />
                )}
              </button>

              {/* Sub Items */}
              {hasSubItems && isActive && !collapsed && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-muted pl-3">
                  {item.subItems!.map((subItem) => {
                    const isSubActive = getActiveSubView(item.id) === subItem.id;
                    const SubIcon = subItem.icon;

                    return (
                      <button
                        key={subItem.id}
                        onClick={() => handleItemClick(item, subItem.id)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors",
                          isSubActive
                            ? "bg-muted text-foreground font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        {SubIcon && <SubIcon className="h-4 w-4" />}
                        <span>{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
